import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCollection } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { Card, EmptyState, LoadingSkeleton } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { CATEGORY_LABELS, type RequestDoc, type RequestStatus } from '@makhe/shared';

export const REQUEST_BADGE: Record<RequestStatus, { label: string; cls: string }> = {
  open: { label: 'Open', cls: 'bg-makhe-forest/15 text-makhe-forest' },
  matched: { label: 'Matched', cls: 'bg-makhe-ochre/20 text-makhe-ochre' },
  fulfilled: { label: 'Done', cls: 'bg-makhe-line/60 text-makhe-muted' },
  cancelled: { label: 'Cancelled', cls: 'bg-makhe-line/60 text-makhe-muted' },
  expired: { label: 'Expired', cls: 'bg-makhe-primary/15 text-makhe-primary' },
};

export default function RequestsPage() {
  const { firebaseUser } = useAuth();
  const q = useMemo(() => {
    if (!firebaseUser) return null;
    return query(
      collection(db, 'requests'),
      where('buyerId', '==', firebaseUser.uid),
      orderBy('createdAt', 'desc'),
      limit(50),
    );
  }, [firebaseUser]);
  const { data: requests, loading } = useCollection<RequestDoc>(q);

  return (
    <section className="mx-auto max-w-3xl px-6 py-8">
      <header className="flex items-center justify-between">
        <h1 className="font-display text-3xl">My requests</h1>
        <Link to="/requests/new" className="btn-primary">+ New</Link>
      </header>

      <div className="mt-6 space-y-3">
        {loading && <LoadingSkeleton className="h-24" />}
        {!loading && requests.length === 0 && (
          <EmptyState
            title="No requests yet"
            description="Can't find a service in the feed? Post a request and let creators come to you."
            action={
              <Link to="/requests/new">
                <Button>Post a request</Button>
              </Link>
            }
          />
        )}
        {requests.map((r) => (
          <Link key={r.id} to={`/requests/${r.id}`}>
            <Card className="flex items-center gap-4">
              {r.referenceImageURLs?.[0] ? (
                <img src={r.referenceImageURLs[0]} alt="" loading="lazy" className="h-16 w-16 rounded-lg object-cover" />
              ) : (
                <div className="grid h-16 w-16 place-items-center rounded-lg bg-makhe-line/50 text-xs text-makhe-muted/60">
                  {CATEGORY_LABELS[r.category].charAt(0)}
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{r.title}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-widest ${REQUEST_BADGE[r.status].cls}`}>
                    {REQUEST_BADGE[r.status].label}
                  </span>
                </div>
                <p className="text-xs text-makhe-muted">{CATEGORY_LABELS[r.category]}</p>
                <p className="text-xs text-makhe-muted/80">
                  {r.offerCount} offer{r.offerCount === 1 ? '' : 's'}
                </p>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
}
